/**
 * Putting a clip back into a score.
 *
 * Pasting overwrites, exactly as typing does: the passage takes as much time as it takes,
 * and what was there is silenced under it. It never pushes the rest of the piece later,
 * because that would leave every bar after the caret overfull and unengravable.
 *
 * A clip's lanes go into consecutive parts from the caret down, so a two-stave passage
 * pasted onto the flute lands on the flute and the part under it. A lane that runs off the
 * bottom of the score, or off the end of the piece, is a refusal rather than a silent
 * truncation: losing half of what was pasted without being told is worse than being asked
 * to add a bar first.
 */

import { copyOfEvent } from "../document/create";
import { createId } from "../document/ids";
import type { EventId, MeasureId } from "../document/ids";
import { capacityOf, measureIndexOf, occupiedTicks } from "../document/query";
import type { Direction, MusicEvent, Part, PartMeasure, Score, Spanner } from "../document/types";
import type { Command } from "../commands/types";
import type { Clip, ClipLane, ClipMark } from "./clipboard";
import type { Caret } from "./entry-place";
import { overwrite } from "./entry-voice";

/** Where one pasted event ended up, for the marks that have to find it again. */
interface Landing {
  readonly id: EventId;
  readonly measureId: MeasureId;
  /** Ticks from the start of that bar, which is where a chord symbol over it goes. */
  readonly onset: number;
}

/** One part's bars while they are being written into, before they become commands. */
type Working = Map<MeasureId, PartMeasure>;

/**
 * Write one lane into one part, from `caret` onwards.
 *
 * Grace notes have no length of their own, so they are held back and put in immediately
 * before the note they lean on; a run of them at the very end of a lane has nothing to
 * lean on and is dropped, which is what the notation would mean anyway.
 */
function writeLane(
  score: Score,
  part: Part,
  lane: ClipLane,
  caret: Caret,
  working: Working,
): readonly Landing[] | string {
  let measureId = caret.measureId;
  let onset = caret.onset;
  let pending: MusicEvent[] = [];
  const landings: Landing[] = [];
  const capacityOfBar = (id: MeasureId): number => capacityOf(score, measureIndexOf(score, id));

  for (const [index, source] of lane.events.entries()) {
    const event = copyOfEvent(
      source,
      index === 0,
      index === lane.events.length - 1,
      part.staves.length,
    );
    if (occupiedTicks(event, capacityOfBar(measureId)) === 0) {
      pending = [...pending, event];
      landings.push({ id: event.id, measureId, onset });
      continue;
    }

    if (onset + occupiedTicks(event, capacityOfBar(measureId)) > capacityOfBar(measureId)) {
      const next = score.measures[measureIndexOf(score, measureId) + 1];
      if (next === undefined) return "the passage runs past the last bar of the score";
      measureId = next.id;
      onset = 0;
    }

    const capacity = capacityOfBar(measureId);
    const held = working.get(measureId) ?? part.measures[measureId];
    const voice = held?.voices[caret.voiceIndex];
    if (held === undefined || voice === undefined) return "that part has no such bar or voice";

    const written = overwrite(voice, onset, event, capacity);
    if (typeof written === "string") return written;

    const events = [
      ...written.events.slice(0, written.index),
      ...pending,
      ...written.events.slice(written.index),
    ];
    pending = [];
    working.set(measureId, {
      ...held,
      voices: held.voices.map((one, at) => (at === caret.voiceIndex ? { ...one, events } : one)),
    });
    landings.push({ id: event.id, measureId, onset });
    onset += occupiedTicks(event, capacity);
  }

  return landings;
}

/** Hang the clip's dynamics and words back on the notes they were copied with. */
function withDirections(lane: ClipLane, landings: readonly Landing[], working: Working): void {
  for (const held of lane.directions) {
    const landing = landings[held.at];
    const measure = landing === undefined ? undefined : working.get(landing.measureId);
    if (landing === undefined || measure === undefined) continue;
    const direction: Direction = {
      ...held.direction,
      id: createId("direction"),
      anchor: landing.id,
    };
    working.set(landing.measureId, {
      ...measure,
      directions: [...measure.directions, direction],
    });
  }
}

/** One copied mark that still fits: the bar it landed in and the tick it stands on. */
interface Landed<T> {
  readonly measureId: MeasureId;
  readonly mark: T;
  readonly onset: number;
}

/**
 * Where each copied mark lands, dropping the ones that no longer fit.
 *
 * A mark whose offset would carry it past the end of the bar it landed in is dropped: the
 * note it stood on has been overwritten into a shorter space, and a chord or a figure
 * standing after the barline is not one anyone can read.
 *
 * The bar itself is deliberately not returned. Two marks can land in one bar, and the
 * second must see what the first wrote, so the caller reads the bar as it writes it.
 */
function landedMarks<T extends { readonly onset: number }>(
  score: Score,
  marks: readonly ClipMark<T>[],
  landings: readonly Landing[],
): readonly Landed<T>[] {
  const placed: Landed<T>[] = [];
  for (const held of marks) {
    const landing = landings[held.at];
    if (landing === undefined) continue;
    const onset = landing.onset + held.offset;
    if (onset >= capacityOf(score, measureIndexOf(score, landing.measureId))) continue;
    placed.push({ measureId: landing.measureId, mark: held.mark, onset });
  }
  return placed;
}

/** The clip's chord symbols, written over the notes that have just landed. */
function withHarmonies(
  score: Score,
  lane: ClipLane,
  landings: readonly Landing[],
  working: Working,
): void {
  for (const { measureId, mark, onset } of landedMarks(score, lane.harmonies, landings)) {
    const measure = working.get(measureId);
    if (measure === undefined) continue;
    const harmony = { ...mark, id: createId("harmony"), onset };
    working.set(measureId, {
      ...measure,
      harmonies: [...measure.harmonies.filter((one) => one.onset !== onset), harmony].sort(
        (one, other) => one.onset - other.onset,
      ),
    });
  }
}

/** The clip's figured bass, written under the notes that have just landed. */
function withFigures(
  score: Score,
  lane: ClipLane,
  landings: readonly Landing[],
  working: Working,
): void {
  for (const { measureId, mark, onset } of landedMarks(score, lane.figures, landings)) {
    const measure = working.get(measureId);
    if (measure === undefined) continue;
    const stack = { ...mark, id: createId("figure"), onset };
    working.set(measureId, {
      ...measure,
      figures: [...measure.figures.filter((one) => one.onset !== onset), stack].sort(
        (one, other) => one.onset - other.onset,
      ),
    });
  }
}

/** The clip's slurs and hairpins, redrawn between the notes that were just written. */
function spannersFor(lane: ClipLane, landings: readonly Landing[]): readonly Spanner[] {
  const drawn: Spanner[] = [];
  for (const held of lane.spanners) {
    const from = landings[held.from];
    const to = landings[held.to];
    if (from === undefined || to === undefined) continue;
    drawn.push({ ...held.spanner, id: createId("spanner"), from: from.id, to: to.id });
  }
  return drawn;
}

/**
 * Paste a clip at the caret.
 *
 * The whole thing is one command, so one undo takes back the whole paste however many
 * parts and bars it touched.
 */
export function paste(score: Score, clip: Clip, caret: Caret): Command | string {
  if (clip.lanes.length === 0) return "there is nothing to paste";
  const base = score.parts.findIndex((candidate) => candidate.id === caret.partId);
  if (base < 0) return "that part is not in the score";
  if (base + clip.lanes.length > score.parts.length) {
    return "there are not enough parts below to paste all of it";
  }

  const commands: Command[] = [];
  for (const [offset, lane] of clip.lanes.entries()) {
    const part = score.parts[base + offset];
    if (part === undefined) return "there are not enough parts below to paste all of it";

    const working: Working = new Map<MeasureId, PartMeasure>();
    const into: Caret = {
      ...caret,
      partId: part.id,
      voiceIndex: offset === 0 ? caret.voiceIndex : 0,
    };
    const landings = writeLane(score, part, lane, into, working);
    if (typeof landings === "string") return landings;

    withDirections(lane, landings, working);
    withHarmonies(score, lane, landings, working);
    withFigures(score, lane, landings, working);
    for (const [measureId, measure] of working) {
      commands.push({ kind: "setPartMeasure", partId: part.id, measureId, measure });
    }

    const drawn = spannersFor(lane, landings);
    if (drawn.length > 0) {
      commands.push({
        kind: "setSpanners",
        partId: part.id,
        spanners: [...part.spanners, ...drawn],
      });
    }
  }

  if (commands.length === 0) return "there is nothing to paste";
  return { kind: "batch", label: "paste", commands };
}
